// src/components/Defesa/DefesaList.jsx
import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Pagination
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Visibility as VisibilityIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import defesaService from '../../services/defesaService';
import useAuth from '../../hooks/useAuth';

const DefesaList = ({ data = [] }) => {
  const [defesas, setDefesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmingDefesa, setConfirmingDefesa] = useState(null);

  // Filtros
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Paginação
  const [page, setPage] = useState(1);
  const pageSize = 5;

  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (data && data.length > 0) {
      setDefesas(data);
      setLoading(false);
    } else {
      const fetchDefesas = async () => {
        try {
          setLoading(true);
          const response = await defesaService.listarDefesas();
          setDefesas(response.data);
        } catch (err) {
          console.error('Erro ao carregar defesas:', err);
          setError('Erro ao carregar defesas. Por favor, tente novamente.');
        } finally {
          setLoading(false);
        }
      };
      fetchDefesas();
    }
    // Executa apenas uma vez (sem dependências de "data")
  }, []);

  // Mapeamento para extrair as informações desejadas (sejam planas ou aninhadas)
  const getTitulo = (defesa) => {
    // Tenta acessar a partir do objeto monografia (associação do projeto → proposta)
    return defesa.monografia?.projeto?.proposta?.titulo ||
           defesa.titulo_proposta ||
           'Sem título';
  };

  const getVersao = (defesa) => {
    return defesa.monografia?.versao_documento ||
           defesa.monografia_versao ||
           'N/D';
  };

  const getStatusMonografia = (defesa) => {
    return defesa.monografia?.status ||
           defesa.monografia_status ||
           'N/D';
  };

  const getStatusProjeto = (defesa) => {
    return defesa.monografia?.projeto?.status ||
           defesa.projeto_status ||
           'N/D';
  };

  const getProblemaPesquisa = (defesa) => {
    return defesa.monografia?.projeto?.proposta?.problemaPesquisa ||
           defesa.problema_de_pesquisa ||
           'N/D';
  };

  // Filtragem e ordenação
  const defesasFiltradas = useMemo(() => {
    let lista = [...defesas];
    // Ordena por data_defesa (crescente)
    lista.sort((a, b) => new Date(a.data_defesa) - new Date(b.data_defesa));

    if (statusFilter) {
      lista = lista.filter(defesa => defesa.status === statusFilter);
    }

    if (searchTerm.trim() !== '') {
      const lowerSearch = searchTerm.toLowerCase();
      lista = lista.filter(defesa => {
        // Procura no título (da proposta) ou local
        const titulo = getTitulo(defesa).toLowerCase();
        const local = defesa.local_defesa?.toLowerCase() || '';
        return titulo.includes(lowerSearch) || local.includes(lowerSearch);
      });
    }

    return lista;
  }, [defesas, statusFilter, searchTerm]);

  // Paginação
  const totalDefesas = defesasFiltradas.length;
  const totalPages = Math.ceil(totalDefesas / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const defesasPagina = defesasFiltradas.slice(startIndex, endIndex);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleConfirmarRealizacao = async (defesaId) => {
    try {
      setConfirmingDefesa(defesaId);
      await defesaService.confirmarRealizacao(defesaId);
      // Recarrega os dados
      if (data && data.length > 0) {
        const response = await defesaService.listarDefesasDoAvaliador();
        setDefesas(response.data);
      } else {
        const response = await defesaService.listarDefesas();
        setDefesas(response.data);
      }
    } catch (err) {
      console.error('Erro ao confirmar realização:', err);
      setError('Erro ao confirmar realização da defesa.');
    } finally {
      setConfirmingDefesa(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!defesas || defesas.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ m: 2 }}>
        Nenhuma defesa encontrada.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Lista de Defesas
      </Typography>

      {/* Filtros */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 3
        }}
      >
        <FormControl sx={{ minWidth: 140 }} size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="AGENDADA">Agendada</MenuItem>
            <MenuItem value="REALIZADA">Realizada</MenuItem>
            <MenuItem value="CANCELADA">Cancelada</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Buscar por título ou local"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          sx={{ width: 220 }}
        />

        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={() => {
            setStatusFilter('');
            setSearchTerm('');
            setPage(1);
          }}
        >
          Limpar Filtros
        </Button>

        <Typography variant="body2" sx={{ ml: 'auto' }}>
          Defesas encontradas: <strong>{totalDefesas}</strong>
        </Typography>
      </Box>

      {defesasFiltradas.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nenhuma defesa corresponde aos filtros selecionados.
        </Alert>
      ) : (
        <>
          {defesasPagina.map((defesa, idx) => (
            <Paper
              key={defesa.defesa_id || defesa.id || idx}
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'scale(1.01)'
                }
              }}
            >
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  {getTitulo(defesa)}
                </Typography>

                <Typography variant="body2">
                  <strong>Versão:</strong> {getVersao(defesa)}
                </Typography>

                <Typography variant="body2">
                  <strong>Status Monografia:</strong> {getStatusMonografia(defesa)}
                </Typography>

                <Typography variant="body2">
                  <strong>Status Projeto:</strong> {getStatusProjeto(defesa)}
                </Typography>

                <Typography variant="body2">
                  <strong>Problema de Pesquisa:</strong> {getProblemaPesquisa(defesa)}
                </Typography>

                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Local:</strong> {defesa.local_defesa || 'N/D'}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {new Date(defesa.data_defesa).toLocaleString()}
                  </Typography>
                </Box>

                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Status:</strong>
                  </Typography>
                  <Chip
                    label={defesa.status}
                    size="small"
                    color={
                      defesa.status === 'AGENDADA'
                        ? 'primary'
                        : defesa.status === 'REALIZADA'
                        ? 'success'
                        : defesa.status === 'CANCELADA'
                        ? 'error'
                        : 'default'
                    }
                  />
                </Box>

                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Pode Avaliar?</strong> {defesa.pode_avaliar ? 'Sim' : 'Não'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mt: { xs: 2, sm: 0 } }}>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() =>
                    navigate(`/defesas/${defesa.defesa_id || defesa.id}`)
                  }
                >
                  Ver Detalhes
                </Button>

                {user?.tipo === 'presidente' &&
                  defesa.status === 'AGENDADA' &&
                  defesa.presidente?.usuario?.id === user.id && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() =>
                        handleConfirmarRealizacao(defesa.defesa_id || defesa.id)
                      }
                      disabled={confirmingDefesa === (defesa.defesa_id || defesa.id)}
                    >
                      {confirmingDefesa === (defesa.defesa_id || defesa.id)
                        ? 'Confirmando...'
                        : 'Confirmar Realização'}
                    </Button>
                )}
              </Box>
            </Paper>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              color="primary"
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

DefesaList.propTypes = {
  data: PropTypes.array,
};

export default DefesaList;
